/**
 * DS-internal hook : l'heure d'un compteur qui court seul, à la seconde.
 *
 * Rend l'instant courant en millisecondes. Tant que `anchor` est posé (l'instant d'où le compteur
 * compte ses secondes), l'heure est relue quatre fois par seconde — le chiffre change au plus un
 * quart de seconde après la seconde — mais le compteur ne se redessine que quand la seconde pleine
 * écoulée depuis `anchor` change : une fois par seconde, pas quatre. `anchor` absent, rien ne tourne.
 */
import { useEffect, useRef, useState } from "react"

/** Quatre lectures par seconde : le chiffre change au plus un quart de seconde après la seconde. */
const TICK_MS = 250

export function useSecondClock(anchor: number | null): number {
    const [now, setNow] = useState(() => Date.now())
    // L'heure rendue, lue par les lectures sans passer par l'état : une lecture de la même seconde
    // ne déclenche rien, pas même un rendu que React jetterait.
    const shown = useRef(now)

    useEffect(() => {
        if (anchor == null) return undefined
        const second = (at: number) => Math.floor((at - anchor) / 1000)
        const tick = () => {
            const current = Date.now()
            if (second(shown.current) === second(current)) return
            shown.current = current
            setNow(current)
        }
        tick()
        const timer = setInterval(tick, TICK_MS)
        return () => clearInterval(timer)
    }, [anchor])

    return now
}
